import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  listBanQueue,
  identityKindDoAlvo,
  rotuloDaIdentidade,
  type BanCandidate,
  type IdentityKind,
} from '../../services/banQueue';
import { ordenarFilaDeBan, rotuloDoTier, corDoTier } from '../../lib/evidence';
import { EvidenceLine } from '../../components/EvidenceLine';
import {
  PERIOD_KEYS,
  rotuloDoPeriodo,
  rotuloDaJanela,
  type PeriodKey,
} from '../../lib/period';

// ============================================================
// Busca por conta / jogador
// ============================================================
// "precisamos criar pesquisas pra pesquisar nomes das contas".
//
// ⚠️ O QUE ESTA BUSCA É, DITO NA TELA E NÃO SÓ AQUI
//   O backend NÃO tem busca por pedaço de nome sobre todas as contas — só
//   `/admin/ban-queue?target=` com o valor COMPLETO. Então esta tela faz duas
//   coisas, e diz qual é qual:
//
//   1. FILTRA por substring os candidatos com evidência na janela. É o caso de
//      uso real ("acha o cheater cujo nome começa com…"), e é honesto: só
//      aparece quem tem evidência forte no período.
//   2. ABRE DIRETO qualquer conta pelo nome exato, tenha evidência ou não —
//      o histórico vem de `/admin/events`, que aceita account_id/user_id.
//
//   Fingir que (1) varre a base inteira seria pior que a limitação: o operador
//   buscaria um nome, não acharia, e concluiria "está limpo" quando o certo é
//   "não tem evidência forte nesta janela".
// ============================================================

export function Accounts() {
  const [termo, setTermo] = useState('');
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [kindExato, setKindExato] = useState<IdentityKind>('account_id');
  const [candidatos, setCandidatos] = useState<BanCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Piso `strong`: é o mínimo que o backend aceita. Não existe fila de
    // "contextual" — rebaixar transformaria a busca numa lista de suspeitas.
    listBanQueue(period, 'strong')
      .then((r) => { if (!cancelled) setCandidatos(r.data ?? []); })
      .catch((e) => {
        if (!cancelled) {
          setCandidatos([]);
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const filtrados = useMemo(() => {
    const q = termo.trim().toLowerCase();
    const base = q === ''
      ? candidatos
      : candidatos.filter((c) => c.target_value.toLowerCase().includes(q));
    return ordenarFilaDeBan(base);
  }, [candidatos, termo]);

  function abrirExato(e: React.FormEvent): void {
    e.preventDefault();
    const v = termo.trim();
    if (!v) return;
    nav(`/accounts/${kindExato}/${encodeURIComponent(v)}`);
  }

  return (
    <div>
      <header className="mb-6">
        <h2 className="text-2xl font-bold text-gray-200 mb-1">Contas e jogadores</h2>
        <p className="text-xs text-gray-500 mb-3">
          Digite parte do nome para filtrar quem tem evidência forte {rotuloDaJanela(period)},
          ou o nome exato para abrir o histórico de qualquer conta.
        </p>

        <form onSubmit={abrirExato} className="flex flex-wrap gap-3 items-end">
          <div>
            <label htmlFor="busca-conta" className="block text-xs text-gray-400 mb-1">
              Nome da conta ou do jogador
            </label>
            <input
              id="busca-conta"
              type="text"
              value={termo}
              onChange={(ev) => setTermo(ev.target.value)}
              placeholder="ex: harness-faseA"
              className="bg-[#161a1f] border border-[#2a2f36] rounded px-3 py-1.5 text-sm text-gray-200 w-72"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Janela</label>
            <div className="flex gap-1">
              {PERIOD_KEYS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setPeriod(k)}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    period === k
                      ? 'border-red-700 bg-red-950/40 text-red-300'
                      : 'border-[#2a2f36] bg-[#161a1f] text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  {rotuloDoPeriodo(k)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="kind-exato" className="block text-xs text-gray-400 mb-1">
              Abrir nome exato como
            </label>
            <select
              id="kind-exato"
              value={kindExato}
              onChange={(ev) => setKindExato(ev.target.value as IdentityKind)}
              className="bg-[#161a1f] border border-[#2a2f36] rounded px-3 py-1.5 text-sm text-gray-200"
            >
              <option value="account_id">conta</option>
              <option value="user_id">jogador</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={termo.trim() === ''}
            className="px-4 py-1.5 text-sm rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Abrir histórico
          </button>
        </form>
      </header>

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Carregando...</p>
      ) : error ? (
        // Mesma razão da fila de ban: erro e lista vazia não podem aparecer
        // juntos, porque a leitura confortável ("está limpo") é a errada.
        null
      ) : filtrados.length === 0 ? (
        <p className="text-gray-500 text-sm">
          {termo.trim()
            ? `Nenhuma conta com evidência forte ${rotuloDaJanela(period)} casa "${termo.trim()}". Isso NÃO quer dizer que a conta está limpa — use "Abrir histórico" para ver os registros dela.`
            : `Nenhuma conta com evidência forte ${rotuloDaJanela(period)}.`}
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-2">
            {filtrados.length} conta(s) com evidência forte {rotuloDaJanela(period)}
            {termo.trim() && ` casando "${termo.trim()}"`} — mais grave primeiro.
          </p>
          <div className="space-y-2">
            {filtrados.map((c) => {
              const kind = identityKindDoAlvo(c.target_kind);
              return (
                <div
                  key={`${c.target_kind}:${c.target_value}`}
                  className="bg-[#161a1f] border border-[#2a2f36] rounded p-3 hover:border-[#3a3f46] transition"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-[#2a2f36] text-gray-400">
                      {rotuloDaIdentidade(c.target_kind)}
                    </span>
                    <Link
                      to={`/accounts/${kind}/${encodeURIComponent(c.target_value)}`}
                      className="font-mono text-sm text-gray-100 hover:text-red-300 hover:underline break-all"
                    >
                      {c.target_value}
                    </Link>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${corDoTier(c.top_tier)}`}>
                      {rotuloDoTier(c.top_tier)}
                    </span>
                    {c.active_sanction && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded border border-gray-600 bg-gray-800 text-gray-300">
                        já sancionado ({c.active_sanction.sanction_type})
                      </span>
                    )}
                    <span className="text-xs text-gray-500 ml-auto tabular-nums">
                      risco {c.evidence_score} · {c.total_hits} ocorrência(s) ·{' '}
                      {c.session_uuids.length} sessão(ões)
                    </span>
                  </div>

                  {c.evidence.length > 0 && (
                    <div className="space-y-1 mt-2">
                      {c.evidence.map((ev, i) => (
                        <EvidenceLine key={ev.sample?.event_id || `${c.target_value}-${i}`} sinal={ev} />
                      ))}
                    </div>
                  )}

                  <div className="mt-2 text-[11px] text-gray-500 flex flex-wrap gap-3">
                    {c.first_seen && <span>desde {new Date(c.first_seen).toLocaleDateString()}</span>}
                    {c.last_seen && <span>até {new Date(c.last_seen).toLocaleDateString()}</span>}
                    <Link
                      to={`/accounts/${kind}/${encodeURIComponent(c.target_value)}`}
                      className="text-blue-400 hover:underline"
                    >
                      registros por dia/semana/mês →
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default Accounts;
